"use client";

import React, { useEffect, useRef, useState } from 'react';
import { useKnowledgeGraph } from '../hooks/useKnowledgeGraph';
import { useGlobalState } from './GlobalStateContext';
import dynamic from 'next/dynamic';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2 } from 'lucide-react';

// Dynamically import the graph components to avoid SSR issues
const ForceGraph2D = dynamic(() => import('react-force-graph-2d'), { ssr: false });
const ForceGraph3D = dynamic(() => import('react-force-graph-3d'), { ssr: false });

// Graph data interface
interface GraphData {
  nodes: Array<{
    id: string;
    name: string;
    val?: number;
    color?: string;
    type?: string;
  }>;
  links: Array<{
    source: string;
    target: string;
    value?: number;
    label?: string;
  }>;
}

// Memory node colors by type
const NODE_COLORS = {
  omi: '#7C3AED', // purple
  loob: '#10B981', // green
  user: '#3B82F6', // blue
  default: '#6B7280', // gray
};

interface MemoryGraphProps {
  userId: string;
  memoryId?: string;
  className?: string;
}

export function MemoryGraph({ userId, memoryId, className = '' }: MemoryGraphProps) {
  const { getMemoryConnections } = useKnowledgeGraph(userId);
  const [graphData, setGraphData] = useState<{ nodes: any[], links: any[] } | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'2d' | '3d'>('2d');
  
  const graphRef = useRef<any>(null);
  
  useEffect(() => {
    const fetchGraphData = async () => {
      if (!memoryId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await getMemoryConnections(memoryId);
        
        // Handle empty graph data
        if (!data || !data.nodes || !data.links || data.nodes.length === 0) {
          setError('No memory connections found for this memory. Try creating more memories to build connections.');
          setGraphData({ nodes: [], links: [] });
          return;
        }
        
        // Process data for visualization
        const processedData = {
          nodes: data.nodes.map((node: any) => ({
            ...node,
            id: node.id || node._id,
            label: node.content ? (node.content.substring(0, 30) + (node.content.length > 30 ? '...' : '')) : 'Unknown',
            color: node.type === 'memory' ? '#ff6b6b' : 
                  node.type === 'entity' ? '#4ecdc4' : 
                  node.type === 'concept' ? '#ffd166' : '#6b5b95'
          })),
          links: data.links.map((link: any) => ({
            ...link,
            source: link.source || link.from,
            target: link.target || link.to,
            value: link.weight || 1
          }))
        };
        
        setGraphData(processedData);
      } catch (err) {
        console.error('Error fetching memory connections:', err);
        setError(`Error connecting to the knowledge graph API. The Smithery Knowledge Graph service may be unavailable or experiencing issues. Please check your server configuration and ensure the \`SMITHERY_API_KEY\` is set correctly in the \`.env.local\` file on the server, and that the API endpoints are correctly configured.`);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchGraphData();
  }, [memoryId, userId, getMemoryConnections]);
  
  const handleZoomToFit = () => {
    if (graphRef.current) {
      // Time delay to ensure the graph is rendered
      setTimeout(() => {
        graphRef.current.zoomToFit(400, 50);
      }, 500);
    }
  };
  
  if (isLoading) {
    return (
      <div className={`flex justify-center items-center p-8 bg-card rounded-lg shadow ${className}`}>
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-3 text-muted-foreground">Loading memory connections...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`p-6 bg-destructive/10 rounded-lg shadow text-center ${className}`}>
        <h3 className="text-lg font-semibold mb-2 text-destructive">Knowledge Graph Error</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <div className="bg-card p-4 rounded-md text-sm mb-4">
          <p className="font-semibold mb-2">Possible solutions:</p>
          <ul className="list-disc list-inside text-left">
            <li>Ensure the Smithery API key is configured on the server</li>
            <li>Check if the Smithery Knowledge Graph service is operational</li>
            <li>Try refreshing the page or creating more memories</li>
            <li>Verify you're using the correct API endpoints in your configuration</li>
          </ul>
        </div>
      </div>
    );
  }
  
  if (!graphData || !memoryId) {
    return (
      <div className={`p-6 bg-card rounded-lg shadow text-center ${className}`}>
        <p className="text-muted-foreground">Select a memory to view its connections.</p>
      </div>
    );
  }
  
  // If graph data is empty
  if (graphData.nodes.length === 0) {
    return (
      <div className={`p-6 bg-card rounded-lg shadow text-center ${className}`}>
        <p className="text-muted-foreground">No connections found for this memory.</p>
        <p className="text-sm text-muted-foreground mt-2">
          Create more memories to build connections in your knowledge graph.
        </p>
      </div>
    );
  }
  
  return (
    <div className={`bg-card rounded-lg shadow-md overflow-hidden ${className}`}>
      <div className="p-4 border-b">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Memory Connections</h3>
          <div className="flex gap-2">
            <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as '2d' | '3d')}>
              <TabsList>
                <TabsTrigger value="2d">2D View</TabsTrigger>
                <TabsTrigger value="3d">3D View</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button size="sm" variant="outline" onClick={handleZoomToFit}>
              Fit Graph
            </Button>
          </div>
        </div>
      </div>
      
      <div className="h-[400px] w-full relative">
        {viewMode === '2d' ? (
          <ForceGraph2D
            ref={graphRef}
            graphData={graphData}
            nodeLabel={(node: any) => node.label || node.content || 'Unknown'}
            linkDirectionalArrowLength={3.5}
            linkDirectionalArrowRelPos={1}
            linkCurvature={0.25}
            backgroundColor="rgba(0,0,0,0)"
            nodeColor={(node: any) => node.color}
            linkColor={() => 'rgba(160, 160, 160, 0.6)'}
            onNodeClick={(node: any) => {
              // Set node as the focus and zoom in
              if (graphRef.current) {
                graphRef.current.centerAt(node.x, node.y, 1000);
                graphRef.current.zoom(2, 1000);
              }
            }}
          />
        ) : (
          <ForceGraph3D
            ref={graphRef}
            graphData={graphData}
            nodeLabel={(node: any) => node.label || node.content || 'Unknown'}
            backgroundColor="rgba(0,0,0,0)"
            nodeColor={(node: any) => node.color}
            linkColor={() => 'rgba(160, 160, 160, 0.6)'}
          />
        )}
      </div>
    </div>
  );
} 